import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getOrganizationForPrint } from '../../utils/organizationHelper';
import './PrintHeader.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nadeedapi.urapp4u.com/api';
const getLogoUrl = (logoPath) => {
  if (!logoPath) return null;
  const baseUrl = API_BASE_URL.replace('/api', '');
  return `${baseUrl}${logoPath}`;
};

/**
 * Global Print Header Component
 * Displays organization logo and details on all print pages
 * 
 * @param {Object} props
 * @param {string} props.title - Optional title to display (e.g., "Property Details", "Receipt Voucher")
 * @param {string} props.subtitle - Optional subtitle
 * @param {string} props.layout - Layout style: 'standard' (default) or 'voucher' (3-column layout)
 * @param {boolean} props.showDate - Whether to show print date (default: true)
 */
const PrintHeader = ({ title, subtitle, layout = 'standard', showDate = true }) => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOrganization = async () => {
      try {
        const org = await getOrganizationForPrint();
        setOrganization(org);
      } catch (error) {
        console.error('Error loading organization for print header:', error);
      } finally {
        setLoading(false);
      }
    };
    loadOrganization();
  }, []);

  if (loading) {
    return null; // Don't show anything while loading
  }

  if (!organization) {
    // Fallback if no organization found
    return (
      <div className="print-header">
        <div className="print-header-content">
          <div className="print-logo-section">
            <img src="/logo.png" alt="Logo" className="print-logo" />
            <h1 className="print-company-name">Agar Live</h1>
          </div>
        </div>
        {title && (
          <>
            <div className="print-divider"></div>
            <div className="print-title">
              <h2>{title}</h2>
              {subtitle && <p className="print-subtitle">{subtitle}</p>}
              {showDate && <p className="print-date">{t('common.printedOn', 'Printed on')}: {new Date().toLocaleDateString()}</p>}
            </div>
          </>
        )}
      </div>
    );
  }

  const orgNameAr = organization.organizationNameAr || organization.OrganizationNameAr || '';
  const orgNameEng = organization.organizationNameEng || organization.OrganizationNameEng || '';
  const crNumber = organization.crNumber || organization.CRNumber || '';
  const vatNumber = organization.vatNumber || organization.VATNumber || '';
  const poBox = organization.poBox || organization.POBox || '';
  const streetName = organization.streetName || organization.StreetName || '';
  const city = organization.city || organization.City;
  const cityNameAr = city?.cityNameAr || city?.CityNameAr || '';
  const cityNameEng = city?.cityNameEng || city?.CityNameEng || '';
  const telephoneNumber = organization.telephoneNumber || organization.TelephoneNumber || '';
  const mobileNumber = organization.mobileNumber || organization.MobileNumber || '';
  const email = organization.email || organization.Email || '';
  const website = organization.website || organization.Website || '';
  const logoPath = organization.logo || organization.Logo;
  const logoUrl = getLogoUrl(logoPath);

  // Voucher layout (3-column layout for vouchers)
  if (layout === 'voucher') {
    return (
      <div className="print-header print-header-voucher">
        <div className="print-header-voucher-content">
          {/* Right Side - Arabic */}
          <div className="print-header-voucher-right">
            {orgNameAr && (
              <div className="print-org-name-ar">{orgNameAr}</div>
            )}
            {crNumber && (
              <div className="print-org-detail">س.ت {crNumber}</div>
            )}
            {vatNumber && (
              <div className="print-org-detail">الرقم الضريبي {vatNumber}</div>
            )}
            {poBox && (
              <div className="print-org-detail">ص.ب {poBox}</div>
            )}
            {streetName && (
              <div className="print-org-detail">
                العنوان: {streetName}
                {cityNameAr && ` - ${cityNameAr}`}
              </div>
            )}
            {telephoneNumber && (
              <div className="print-org-detail">هاتف: {telephoneNumber}</div>
            )}
            {mobileNumber && (
              <div className="print-org-detail">جوال: {mobileNumber}</div>
            )}
            {email && (
              <div className="print-org-detail">البريد: {email}</div>
            )}
          </div>

          {/* Middle - Logo and Title */}
          <div className="print-header-voucher-center">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo"
                className="print-logo-voucher"
                crossOrigin="anonymous"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const fallback = e.target.nextSibling;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
            ) : null}
            <div className="print-logo-fallback" style={{ display: logoUrl ? 'none' : 'flex' }}>
              LOGO
            </div>
            {title && (
              <>
                <div className="print-voucher-title-ar">{isArabic ? title : subtitle || title}</div>
                <div className="print-voucher-title-eng">{isArabic ? subtitle || title : title}</div>
              </>
            )}
          </div>

          {/* Left Side - English */}
          <div className="print-header-voucher-left">
            {orgNameEng && (
              <div className="print-org-name-eng">{orgNameEng}</div>
            )}
            {crNumber && (
              <div className="print-org-detail">C.R. {crNumber}</div>
            )}
            {vatNumber && (
              <div className="print-org-detail">VAT: {vatNumber}</div>
            )}
            {poBox && (
              <div className="print-org-detail">P.O. Box {poBox}</div>
            )}
            {streetName && (
              <div className="print-org-detail">
                Address: {streetName}
                {cityNameEng && ` - ${cityNameEng}`}
              </div>
            )}
            {telephoneNumber && (
              <div className="print-org-detail">Tel: {telephoneNumber}</div>
            )}
            {mobileNumber && (
              <div className="print-org-detail">Mobile: {mobileNumber}</div>
            )}
            {email && (
              <div className="print-org-detail">Email: {email}</div>
            )}
            {website && (
              <div className="print-org-detail">Web: {website}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Standard layout (centered layout for regular prints)
  return (
    <div className="print-header">
      <div className="print-header-content">
        <div className="print-logo-section">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Logo"
              className="print-logo"
              crossOrigin="anonymous"
              onError={(e) => {
                e.target.style.display = 'none';
                const fallback = e.target.nextSibling;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
          ) : (
            <img src="/logo.png" alt="Logo" className="print-logo" />
          )}
          <div className="print-logo-fallback" style={{ display: logoUrl ? 'none' : 'flex' }}>
            LOGO
          </div>
          <div className="print-org-info">
            {orgNameEng && (
              <h1 className="print-company-name">{orgNameEng}</h1>
            )}
            {orgNameAr && (
              <h2 className="print-company-name-ar">{orgNameAr}</h2>
            )}
          </div>
        </div>
        <div className="print-org-details">
          {crNumber && (
            <div className="print-org-detail-item">
              <span className="print-detail-label">{t('organization.crNumber', 'C.R.')}:</span>
              <span className="print-detail-value">{crNumber}</span>
            </div>
          )}
          {vatNumber && (
            <div className="print-org-detail-item">
              <span className="print-detail-label">{t('organization.vatNumber', 'VAT')}:</span>
              <span className="print-detail-value">{vatNumber}</span>
            </div>
          )}
          {poBox && (
            <div className="print-org-detail-item">
              <span className="print-detail-label">{t('organization.poBox', 'P.O. Box')}:</span>
              <span className="print-detail-value">{poBox}</span>
            </div>
          )}
          {streetName && (
            <div className="print-org-detail-item">
              <span className="print-detail-label">{t('organization.address', 'Address')}:</span>
              <span className="print-detail-value">
                {streetName}
                {cityNameEng && ` - ${cityNameEng}`}
                {cityNameAr && ` (${cityNameAr})`}
              </span>
            </div>
          )}
          {(telephoneNumber || mobileNumber) && (
            <div className="print-org-detail-item">
              <span className="print-detail-label">{t('organization.contact', 'Contact')}:</span>
              <span className="print-detail-value">
                {telephoneNumber && `Tel: ${telephoneNumber}`}
                {telephoneNumber && mobileNumber && ' | '}
                {mobileNumber && `Mobile: ${mobileNumber}`}
              </span>
            </div>
          )}
          {email && (
            <div className="print-org-detail-item">
              <span className="print-detail-label">{t('organization.email', 'Email')}:</span>
              <span className="print-detail-value">{email}</span>
            </div>
          )}
          {website && (
            <div className="print-org-detail-item">
              <span className="print-detail-label">{t('organization.website', 'Website')}:</span>
              <span className="print-detail-value">{website}</span>
            </div>
          )}
        </div>
      </div>
      {title && (
        <>
          <div className="print-divider"></div>
          <div className="print-title">
            <h2>{title}</h2>
            {subtitle && <p className="print-subtitle">{subtitle}</p>}
            {showDate && (
              <p className="print-date">
                {t('common.printedOn', 'Printed on')}: {new Date().toLocaleDateString()}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PrintHeader;

